/**
 * API client for /api/v1: session cookie auth, CSRF header on writes, an Idempotency-Key per
 * POST, and problem+json errors surfaced as ApiError with a readable message.
 */
import type { Problem } from "@brecx/shared";
import { useCallback, useEffect, useRef, useState } from "react";

let csrf = "";
export const setCsrf = (t: string) => {
  csrf = t;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public problem: Partial<Problem>,
  ) {
    super(problem.detail ?? problem.title ?? `Request failed (${status})`);
  }
}

/**
 * A random UUID v4 for Idempotency-Key. crypto.randomUUID exists only in secure contexts
 * (https, localhost); on a LAN address over http, build one from crypto.getRandomValues.
 */
export function uuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET") headers["x-csrf-token"] = csrf;
  if (method === "POST") headers["idempotency-key"] = uuid();
  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/auth/") && location.pathname !== "/login") {
      location.assign(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
    }
    throw new ApiError(res.status, data ?? {});
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export interface Resource<T> {
  data: T | null;
  error: ApiError | Error | null;
  loading: boolean;
  reload: () => void;
}

/**
 * GET answers kept in memory (the API is far from the database, so every read costs real time):
 * a page or tab seen before paints at once from here while a fresh copy is fetched. Reads of the
 * same path at the same moment share one request. Newest-used entries are kept.
 */
const CACHE_MAX = 300;
const cache = new Map<string, { data: unknown; at: number }>();
const inflight = new Map<string, Promise<unknown>>();

function remember(path: string, data: unknown) {
  cache.delete(path);
  cache.set(path, { data, at: Date.now() });
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value!);
}

/** GET through the cache: one request per path at a time; the answer is remembered. */
function fetchShared<T>(path: string, fresh = false): Promise<T> {
  const running = inflight.get(path);
  if (running && !fresh) return running as Promise<T>;
  const p = request<T>("GET", path)
    .then((d) => {
      remember(path, d);
      return d;
    })
    .finally(() => {
      if (inflight.get(path) === p) inflight.delete(path);
    });
  inflight.set(path, p);
  return p;
}

/** Warm the cache for a page likely to open next; skipped when a recent copy is there. */
export function prefetch(path: string, maxAgeMs = 60_000): void {
  const hit = cache.get(path);
  if (hit && Date.now() - hit.at < maxAgeMs) return;
  if (inflight.has(path)) return;
  void fetchShared(path).catch(() => {});
}

/**
 * GET a path and re-fetch whenever it changes; `reload()` fetches again. A remembered answer for
 * the path shows at once (stale-while-revalidate); stale responses are dropped.
 */
export function useApi<T>(path: string | null): Resource<T> {
  const [state, setState] = useState<{ data: T | null; error: Error | null; loading: boolean }>(() => ({
    data: path ? ((cache.get(path)?.data as T | undefined) ?? null) : null,
    error: null,
    loading: !!path,
  }));
  const [tick, setTick] = useState(0);
  const seq = useRef(0);
  useEffect(() => {
    if (!path) return;
    const mine = ++seq.current;
    const hit = cache.get(path)?.data as T | undefined;
    setState((s) => ({ data: hit ?? s.data, loading: true, error: null }));
    // reload() after a write must not reuse a read that started before it
    fetchShared<T>(path, tick > 0)
      .then((data) => mine === seq.current && setState({ data, error: null, loading: false }))
      .catch((error: Error) => mine === seq.current && setState((s) => ({ ...s, error, loading: false })));
  }, [path, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/**
 * Subscribe to server events (SSE) of the given types; returns nothing, calls `on` per event.
 * After a dropped connection (API restart, network) it calls `on("reconnect", null)` once the
 * stream is back, since events sent in between are lost: subscribers re-read what they show.
 */
export function useEvents(types: string[], on: (type: string, data: unknown) => void): void {
  const cb = useRef(on);
  cb.current = on;
  const key = types.join(",");
  useEffect(() => {
    const es = new EventSource("/api/v1/events");
    let dropped = false;
    es.onerror = () => {
      dropped = true;
    };
    es.onopen = () => {
      if (!dropped) return;
      dropped = false;
      cb.current("reconnect", null);
    };
    const handlers = key.split(",").map((t) => {
      const h = (e: MessageEvent) => cb.current(t, JSON.parse(e.data));
      es.addEventListener(t, h);
      return [t, h] as const;
    });
    return () => {
      for (const [t, h] of handlers) es.removeEventListener(t, h);
      es.close();
    };
  }, [key]);
}
